/**
 * Compliance Approvals page
 *
 * Reviewer-facing inbox + history for content approvals (templates, media,
 * workflows). Gated by useComplianceMode().canApproveContent — viewable only
 * by org_admin, super_admin, or compliance_reviewer.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Loader2, Inbox, ShieldCheck, XCircle, Mail, FileText, Users } from 'lucide-react';
import { approvalsAPI, ApprovalRequest, ApprovalDecision } from '@/services/api/approvals';
import { useComplianceMode } from '@/hooks/useComplianceMode';

import { config as productConfig } from '@/src/config';
const WEAVEIN_API = (productConfig.weaveinApi.baseUrl ?? '').trim();

async function fetchSubjectDetail(kind: string, id: number): Promise<any | null> {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;

  let url = '';
  if (kind === 'communication_template') url = `${WEAVEIN_API}/api/communication-templates/${id}`;
  else if (kind === 'media_asset') url = `${WEAVEIN_API}/api/media/assets/${id}`;
  else return null;
  try {
    const res = await fetch(url, { headers });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

interface Props {
  /** When set, renders a back link instead of standalone-page header. */
  onBack?: () => void;
  /** Override the hook-derived isRegulated value (parent has the authoritative one from /api/compliance/settings). */
  isRegulatedOverride?: boolean;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-900',
  approved: 'bg-green-100 text-green-900',
  rejected: 'bg-red-100 text-red-900',
  withdrawn: 'bg-paper-2 text-ink-700',
  superseded: 'bg-paper-2 text-ink-700',
};

export default function ApprovalsPage({ onBack, isRegulatedOverride }: Props = {}) {
  const { canApproveContent, isRegulated: hookRegulated } = useComplianceMode();
  const isRegulated = isRegulatedOverride ?? hookRegulated;
  const [tab, setTab] = useState<'pending' | 'closed'>('pending');
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRequest, setActiveRequest] = useState<any | null>(null);
  const [subjectDetail, setSubjectDetail] = useState<any | null>(null);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const list = await approvalsAPI.listRequests(
        tab === 'pending'
          ? { status: 'pending', limit: 100 }
          : { limit: 100 },
      );
      setRequests(tab === 'closed' ? list.filter((r) => r.status !== 'pending') : list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tab]);

  const openDetail = async (id: number) => {
    const detail = await approvalsAPI.getRequest(id);
    setActiveRequest(detail);
    setDecisionNotes('');
    setSubjectDetail(null);
    if (detail) {
      setSubjectLoading(true);
      try {
        const subj = await fetchSubjectDetail(detail.subject_kind, detail.subject_id);
        setSubjectDetail(subj);
      } finally {
        setSubjectLoading(false);
      }
    }
  };

  const decide = async (decision: ApprovalDecision) => {
    if (!activeRequest) return;
    setDecisionLoading(true);
    try {
      await approvalsAPI.decide(activeRequest.id, decision, decisionNotes || undefined);
      setActiveRequest(null);
      load();
    } finally {
      setDecisionLoading(false);
    }
  };

  if (!onBack && !canApproveContent) {
    return (
      <div className="container mx-auto p-8 max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            You don't have permission to view the approval queue.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={onBack ? '' : 'container mx-auto py-8 px-4 max-w-6xl'}>
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-700 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Compliance Settings
        </button>
      )}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Inbox className="h-6 w-6" /> Compliance Approvals
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Review and decide on content submitted for compliance approval.
          {!isRegulated && (
            <span className="ml-1 italic">
              This org is in standard mode — approvals are advisory only.
            </span>
          )}
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">
          <RequestList requests={requests} loading={loading} onOpen={openDetail} />
        </TabsContent>
        <TabsContent value="closed">
          <RequestList requests={requests} loading={loading} onOpen={openDetail} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!activeRequest} onOpenChange={(o) => !o && setActiveRequest(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeRequest?.subject_label || `Request #${activeRequest?.id}`}
            </DialogTitle>
            <DialogDescription>
              Review the submission below, then approve, request changes, or reject.
            </DialogDescription>
          </DialogHeader>
          {activeRequest && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Badge variant="outline">{activeRequest.subject_kind.replace(/_/g, ' ')}</Badge>
                <Badge className={STATUS_BADGE[activeRequest.status] || 'bg-paper-2'}>
                  {activeRequest.status}
                </Badge>
                <span className="text-ink-500">
                  Step {activeRequest.current_step} of {(activeRequest.steps?.length || 1)}
                </span>
                <span className="text-ink-400">·</span>
                <span className="text-ink-500">
                  Submitted {new Date(activeRequest.submitted_at).toLocaleString()}
                </span>
              </div>

              {activeRequest.summary && (
                <div className="text-sm">
                  <div className="text-ink-500 mb-1">Summary</div>
                  <div>{activeRequest.summary}</div>
                </div>
              )}

              {/* Required approvers — workflow steps */}
              {activeRequest.steps && activeRequest.steps.length > 0 && (
                <div className="text-sm">
                  <div className="text-ink-500 mb-2 flex items-center gap-1">
                    <Users className="h-3 w-3" /> Required approvers
                  </div>
                  <ol className="space-y-2">
                    {activeRequest.steps.map((step: any, i: number) => {
                      const stepNum = step.order ?? i + 1;
                      const isCurrent = stepNum === activeRequest.current_step && activeRequest.status === 'pending';
                      const isDone = stepNum < activeRequest.current_step || (stepNum === activeRequest.current_step && activeRequest.status === 'approved');
                      return (
                        <li
                          key={i}
                          className={`flex items-center justify-between rounded border p-2 text-xs ${
                            isCurrent ? 'border-amber-400 bg-amber-50 dark:bg-amber-950 dark:border-amber-700' : ''
                          }`}
                        >
                          <div>
                            <div className="font-medium">
                              Step {stepNum} · {step.name || step.role}
                            </div>
                            <div className="text-ink-500">
                              Approver role: <code className="px-1 bg-paper-2 rounded">{step.role}</code>
                              {step.approvers_n ? ` · requires ${step.approvers_n}` : ''}
                            </div>
                          </div>
                          {isDone && (
                            <Badge className="bg-green-100 text-green-900 text-xs">Approved</Badge>
                          )}
                          {isCurrent && (
                            <Badge className="bg-amber-100 text-amber-900 text-xs">Current</Badge>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {/* Subject preview — varies by kind */}
              <div className="text-sm">
                <div className="text-ink-500 mb-2 flex items-center gap-1">
                  {activeRequest.subject_kind === 'communication_template' ? (
                    <Mail className="h-3 w-3" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}{' '}
                  Submission preview
                </div>
                <div className="rounded border p-3 bg-paper space-y-2">
                  {subjectLoading ? (
                    <div className="text-xs text-ink-500">
                      <Loader2 className="h-3 w-3 inline animate-spin mr-1" />
                      Loading content…
                    </div>
                  ) : !subjectDetail ? (
                    <div className="text-xs text-ink-500">
                      Couldn't load the {activeRequest.subject_kind.replace(/_/g, ' ')} content.
                      It may have been deleted or you don't have access.
                    </div>
                  ) : activeRequest.subject_kind === 'communication_template' ? (
                    <>
                      <div className="text-xs text-ink-500">Type</div>
                      <div className="font-medium">
                        {subjectDetail.template_type} · {subjectDetail.communication_method}
                      </div>
                      {subjectDetail.subject && (
                        <>
                          <div className="text-xs text-ink-500 mt-2">Subject</div>
                          <div className="font-medium">{subjectDetail.subject}</div>
                        </>
                      )}
                      <div className="text-xs text-ink-500 mt-2">Body</div>
                      <pre className="text-xs whitespace-pre-wrap font-sans bg-surface dark:bg-gray-950 p-2 rounded border">
                        {subjectDetail.body}
                      </pre>
                      {subjectDetail.variable_fields?.length > 0 && (
                        <>
                          <div className="text-xs text-ink-500 mt-2">Variables</div>
                          <div className="flex flex-wrap gap-1">
                            {subjectDetail.variable_fields.map((v: string) => (
                              <Badge key={v} variant="outline" className="text-xs">
                                {`{{${v}}}`}
                              </Badge>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : activeRequest.subject_kind === 'media_asset' ? (
                    <>
                      <div className="font-medium">{subjectDetail.name}</div>
                      <div className="text-xs text-ink-500">
                        {subjectDetail.asset_type}
                        {subjectDetail.regulated_category ? ` · ${subjectDetail.regulated_category}` : ''}
                      </div>
                      {subjectDetail.description && (
                        <div className="text-sm mt-1">{subjectDetail.description}</div>
                      )}
                      {subjectDetail.expiration_date && (
                        <div className="text-xs text-ink-500 mt-2">
                          Expires {subjectDetail.expiration_date}
                        </div>
                      )}
                    </>
                  ) : (
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(subjectDetail, null, 2)}
                    </pre>
                  )}
                </div>
              </div>

              {activeRequest.decisions && activeRequest.decisions.length > 0 && (
                <div className="text-sm">
                  <div className="text-ink-500 mb-1">Decision history</div>
                  <ul className="space-y-2">
                    {activeRequest.decisions.map((d: any, i: number) => (
                      <li key={i} className="rounded border p-2 text-xs">
                        <div>
                          <strong>{d.decided_by_name || `User #${d.decided_by}`}</strong>{' '}
                          <span className="text-ink-500">step {d.step}</span>{' '}
                          <Badge className="ml-1 text-xs">{d.decision}</Badge>
                          <span className="text-ink-400 ml-1">
                            {new Date(d.decided_at).toLocaleString()}
                          </span>
                        </div>
                        {d.notes && <div className="text-ink-500 mt-1">{d.notes}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeRequest.status === 'pending' && (
                <div className="space-y-2">
                  <div className="text-sm font-medium">Decision notes</div>
                  <Textarea
                    rows={3}
                    placeholder="Optional notes — visible in the approval audit trail."
                    value={decisionNotes}
                    onChange={(e) => setDecisionNotes(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}
          {activeRequest?.status === 'pending' && (
            <DialogFooter className="flex flex-row gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => decide('requested_changes')}
                disabled={decisionLoading}
              >
                Request changes
              </Button>
              <Button
                variant="destructive"
                onClick={() => decide('rejected')}
                disabled={decisionLoading}
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button onClick={() => decide('approved')} disabled={decisionLoading}>
                {decisionLoading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-1" />
                )}
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestList({
  requests,
  loading,
  onOpen,
}: {
  requests: ApprovalRequest[];
  loading: boolean;
  onOpen: (id: number) => void;
}) {
  if (loading) {
    return (
      <div className="py-10 text-center text-sm text-ink-500">
        <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (requests.length === 0) {
    return (
      <Card className="mt-4">
        <CardContent className="py-10 text-center text-sm text-ink-500">
          No requests in this view.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2 mt-4">
      {requests.map((r) => (
        <Card
          key={r.id}
          className="cursor-pointer hover:bg-paper dark:hover:bg-gray-900"
          onClick={() => onOpen(r.id)}
        >
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{r.subject_label || `Request #${r.id}`}</span>
                <Badge variant="outline" className="text-xs">
                  {r.subject_kind}
                </Badge>
                <Badge className={STATUS_BADGE[r.status] || 'bg-paper-2'}>{r.status}</Badge>
              </div>
              <div className="text-xs text-ink-500 mt-1">
                Submitted {new Date(r.submitted_at).toLocaleString()} · step {r.current_step}
                {r.workflow_name ? ` · ${r.workflow_name}` : ''}
              </div>
            </div>
            <Button size="sm" variant="outline">
              Review
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
